import React, { useState, useEffect } from "react";
import axios from 'axios';
import {
    Stepper,
    Step,
    StepLabel,
    StepContent,
    Button,
    TextField,
    FormControl,
    MenuItem,
    Select,
    InputLabel,
    Card,
    CardContent,
    Typography,
    Checkbox,
    FormControlLabel
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { makeStyles } from '@material-ui/core/styles';
import emailjs from 'emailjs-com';
import '../Components/sgpform2025.css';

const useStyles = makeStyles((theme) => ({
    rootouter: {
        backgroundColor: '#fff',
        backgroundSize: 'contain',
        backgroundRepeat: 'repeat',
        backgroundPosition: 'bottom',
    },
    divider: {
        backgroundColor: '#002147',
    },
    root: {
        margin: '24px 0px',
        position: 'relative',
        backgroundColor: '#fff',
        maxWidth: '900px',
        top: '-17.5vw',
        padding: '60px 20px 60px 20px',
        boxShadow: '0 0 10px 0 rgba(0,0,0,0.2)',	
        borderRadius: '25px',
        '& p': {
            fontSize: '0.9rem',
        },
        '@media (min-width: 768px)': {
          marginLeft: 'auto',
          marginRight: 'auto',
          padding: '60px 80px 60px 80px',
          borderRadius: '5px'
        }
    },
    redText: {
        color: '#b91c1c',
    },
    stepper: {
        background: 'transparent',
        padding: 0,
        marginBottom: theme.spacing(3),
    },
    stepperVertical: {
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        '& .MuiStepConnector-root': {
            marginLeft: 12,
        }
    },
    stepContent: {
        marginTop: theme.spacing(2),
    },
    sectionTitle: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(1),
        fontWeight: 700,
        color: '#1f2937',
    },
    card: {
        marginBottom: theme.spacing(2),
        borderRadius: 12,
        boxShadow: '0 2px 12px rgba(2,6,23,0.06)',
        border: '1px solid rgba(15, 23, 42, 0.04)',
    },
    cardGroupGrid: {
        display: 'flex',
        flexDirection: 'column'
    },
    cardContentGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 120px 100px 140px',
        gap: theme.spacing(2),
        alignItems: 'top',
        [theme.breakpoints.down('sm')]: {
            gridTemplateColumns: '1fr', // single column on mobile
            gap: theme.spacing(1.25),
            alignItems: 'stretch',
            // ensure child controls expand full width
            '& > *': {
                width: '100%',
            },
            // reduce Card padding a bit on small screens for better fit
            '& $card': {
                padding: theme.spacing(1),
            },
            marginBottom: '30px'
        },
    },
    field: {
        width: '100%',
        paddingRight: '10px'
    },
    addButton: {
        marginTop: theme.spacing(1),
    },
    actionRow: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        marginTop: theme.spacing(3),
    },
    primaryButton: {
        backgroundColor: '#0b488fff',
        borderColor: '#0b488fff',
        color: '#fff',
        marginLeft: theme.spacing(1),
        '&:hover': {
            backgroundColor: '#082f5bff',
            borderColor: '#082f5bff',
            color: '#fff',
        },
    },
    secondaryButton: {
        color: theme.palette.error.main,
    },
    errorText: {
        color: theme.palette.error.main,
        marginTop: theme.spacing(1),
        fontSize: '0.95rem',
    },
    duplicatesBox: {
        marginTop: theme.spacing(2),
        padding: theme.spacing(2),
        borderRadius: 8,
        background: 'rgba(255,235,238,0.7)',
        border: `1px solid ${theme.palette.error.light}`,
        color: theme.palette.error.dark,
    },
    rulesBox: {
        border: '1px solid rgba(15,23,42,0.06)',
        fontSize: '0.9rem',
        borderRadius: 10,
        padding: theme.spacing(2),
        background: '#fafafa',
        maxHeight: 360,
        overflowY: 'auto',
        marginBottom: theme.spacing(2),
    },
}));

export default function Sgp2025CompetitionRegForm() {
    const classes = useStyles();

    const steps = [
        "Rules & Regulations",
        "School Name",
        "Quiz",
        "Dance / Act",
        "MCQ",
        "Painting",
        "Art & Craft",
        "Preview & Submit",
    ];

    const [activeStep, setActiveStep] = useState(0);
    const [errors, setErrors] = useState({});
    const [schoolName, setSchoolName] = useState("");
    const [consentChecked, setConsentChecked] = useState(false);

    const emptyStudent = { name: "", standard: "", age: "", gender: "" };

    const [quizA, setQuizA] = useState([]); // Group A: std 3-5
    const [quizB, setQuizB] = useState([]); // Group B: std 6-9
    const [dance, setDance] = useState([]); // Group B only
    const [mcq, setMcq] = useState([]); // Group B only
    const [painting, setPainting] = useState([]); // Group A only
    const [artCraft, setArtCraft] = useState([]); // Group B only

    const [schoolList, setSchoolList] = useState([]);

    // new fields for step "School Name"
    const [inchargeName, setInchargeName] = useState("");
    const [inchargeWhatsapp, setInchargeWhatsapp] = useState("");
    const [schoolEmail, setSchoolEmail] = useState("");

    useEffect(() => {
        axios.get('https://satprerna.org/satprerna_backend/home/get_school_list_master.php')
        .then(response => {
            setSchoolList(response.data);
        })
    }, []);

    // helpers
    const inRange = (std, min, max) => {
        const s = parseInt(std, 10);
        if (Number.isNaN(s)) return false;
        return s >= min && s <= max;
    };

    const studentKey = (s) =>
        `${(s.name || "").trim().toLowerCase()}|${s.standard}|${s.age}|${(s.gender || "").toLowerCase()}`;

    const anyDuplicateAcross = () => {
        const lists = [
            { name: "quizA", list: quizA },
            { name: "quizB", list: quizB },
            { name: "dance", list: dance },
            { name: "mcq", list: mcq },
            { name: "painting", list: painting },
            { name: "artCraft", list: artCraft },
        ];
        const map = new Map();
        const dupes = [];
        for (const entry of lists) {
            for (const s of entry.list) {
                if (!s.name) continue;
                const k = studentKey(s);
                const prev = map.get(k);
                if (prev && prev !== entry.name) {
                    dupes.push({ student: s, in: [prev, entry.name] });
                } else {
                    map.set(k, entry.name);
                }
            }
        }
        return dupes;
    };

    // NEW: check duplicates within each competition list
    const anyDuplicateWithin = () => {
        const lists = [
            { name: "quizA", list: quizA },
            { name: "quizB", list: quizB },
            { name: "dance", list: dance },
            { name: "mcq", list: mcq },
            { name: "painting", list: painting },
            { name: "artCraft", list: artCraft },
        ];
        const dupes = [];
        for (const entry of lists) {
            const seen = new Map();
            for (const [idx, s] of entry.list.entries()) {
                if (!s.name) continue;
                const k = studentKey(s);
                if (seen.has(k)) {
                    // record duplicate occurrence (you can expand with indices if needed)
                    dupes.push({ student: s, list: entry.name });
                } else {
                    seen.set(k, idx);
                }
            }
        }
        return dupes;
    };

    const validateEmail = (email) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    const validatePhone = (phone) => {
        const digits = (phone || '').replace(/\D/g, '');
        return digits.length === 10;
    };

    const validateStudentList = (list, listKey, groupType) => {
        const studentErrors = {};
        list.forEach((s, idx) => {
            const se = {};
            if (!s.name || !s.name.trim()) se.name = "Student name is required";
            if (!s.standard && s.standard !== 0) se.standard = "Standard is required";
            else if (groupType === "A" && !inRange(s.standard, 3, 5)) se.standard = "Std must be 3-5";
            else if (groupType === "B" && !inRange(s.standard, 6, 9)) se.standard = "Std must be 6-9";

            if (!s.age && s.age !== 0) se.age = "Age is required";
            else if (Number.isNaN(parseInt(s.age, 10)) || parseInt(s.age, 10) <= 0) se.age = "Enter a valid age";

            if (!s.gender) se.gender = "Gender is required";

            if (Object.keys(se).length > 0) studentErrors[idx] = se;
        });
        return Object.keys(studentErrors).length ? { [listKey]: studentErrors } : null;
    };

    const validateStep = (step = activeStep) => {
        const newErrors = {};

        if (step === 0) {
            if (!consentChecked) newErrors.consent = "You must read and agree to the rules to continue";
        }

        if (step === 1) {
            if (!schoolName.trim()) newErrors.schoolName = "School name is required";
            if (!inchargeName.trim()) newErrors.inchargeName = "Incharge name is required";
            if (!inchargeWhatsapp.trim()) newErrors.inchargeWhatsapp = "Whatsapp number is required";
            else if (!validatePhone(inchargeWhatsapp)) newErrors.inchargeWhatsapp = "Enter a valid mobile number (10 digits)";
            if (!schoolEmail.trim()) newErrors.schoolEmail = "School email is required";
            else if (!validateEmail(schoolEmail)) newErrors.schoolEmail = "Enter a valid email address";
        }

        if (step === 2) {
            // Quiz validations (existing)
            const total = quizA.length + quizB.length;
            if (total > 8) newErrors.quiz = "Max 8 students allowed in Quiz (across groups A & B)";
            if (quizA.length > 0 && quizA.length !== 4) newErrors.quizA = "Group A quiz participants must be exactly 4";
            if (quizB.length > 0 && quizB.length !== 4) newErrors.quizB = "Group B quiz participants must be exactly 4";
            if (quizA.some((s) => !inRange(s.standard, 3, 5))) newErrors.quizAStd = "All Group A quiz students must be Std 3-5 (Group A only)";
            if (quizB.some((s) => !inRange(s.standard, 6, 9))) newErrors.quizBStd = "All Group B quiz students must be Std 6-9 (Group B only)";

            // Ensure at least one student from each standard in the group's range is present
            if (quizA.length > 0) {
                const stdsA = new Set(quizA.map((s) => parseInt(s.standard, 10)).filter((n) => !Number.isNaN(n)));
                const requiredA = [3, 4, 5];
                const missingA = requiredA.filter((r) => !stdsA.has(r));
                if (missingA.length > 0) newErrors.quizAStdReq = "Group A quiz must include at least one student from each standard 3, 4 and 5";
            }

            if (quizB.length > 0) {
                const stdsB = new Set(quizB.map((s) => parseInt(s.standard, 10)).filter((n) => !Number.isNaN(n)));
                const requiredB = [6, 7, 8, 9];
                const missingB = requiredB.filter((r) => !stdsB.has(r));
                if (missingB.length > 1) newErrors.quizBStdReq = "Group B quiz must have students from at least three of these standards: 6, 7, 8, and 9";
            }

            // per-student field checks for quiz lists
            const qAerrors = validateStudentList(quizA, 'quizA', 'A');
            const qBerrors = validateStudentList(quizB, 'quizB', 'B');
            if (qAerrors || qBerrors) newErrors.students = { ...(qAerrors || {}), ...(qBerrors || {}) };
        }

        if (step === 3) {
            if (dance.length > 0 && (dance.length < 5 || dance.length > 12)) newErrors.dance = "Dance team must have 5 to 12 students";
            if (dance.some((s) => !inRange(s.standard, 6, 9))) newErrors.danceStd = "All Dance students must be from Std 6-9 (Group B only)";

            const dErrors = validateStudentList(dance, 'dance', 'B');
            if (dErrors) newErrors.students = { ...(newErrors.students || {}), ...dErrors };
        }

        if (step === 4) {
            // MCQ: Group B only, skip-able, max 20
            if (mcq.length > 20) newErrors.mcq = "Max 20 students are allowed in MCQ";
            if (mcq.some((s) => !inRange(s.standard, 6, 9))) newErrors.mcqStd = "All MCQ students must be Std 6-9 (Group B only)";

            const mErrors = validateStudentList(mcq, 'mcq', 'B');
            if (mErrors) newErrors.students = { ...(newErrors.students || {}), ...mErrors };
        }

        if (step === 5) {
            // Painting: Group A only, skip-able, max 10
            if (painting.length > 10) newErrors.painting = "Max 10 students allowed in Painting (Group A only)";
            if (painting.some((s) => !inRange(s.standard, 3, 5))) newErrors.paintingStd = "All Painting students must be Std 3-5 (Group A only)";

            const pErrors = validateStudentList(painting, 'painting', 'A');
            if (pErrors) newErrors.students = { ...(newErrors.students || {}), ...pErrors };
        }

        if (step === 6) {
            // Art & Craft: Group B only, skip-able, max 10
            if (artCraft.length > 10) newErrors.artCraft = "Max 10 students allowed in Art & Craft (Group B only)";
            if (artCraft.some((s) => !inRange(s.standard, 6, 9))) newErrors.artCraftStd = "All Art & Craft students must be Std 6-9 (Group B only)";

            const aErrors = validateStudentList(artCraft, 'artCraft', 'B');
            if (aErrors) newErrors.students = { ...(newErrors.students || {}), ...aErrors };
        }

        // For step-level validation we don't yet check cross-competition duplicates unless user is on final step.
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateAll = () => {
        // run all step validations + duplicates across competitions
        const allErrors = {};
        // consent required
        if (!consentChecked) allErrors.consent = "You must read and agree to the rules to continue";

        if (!schoolName.trim()) allErrors.schoolName = "School name is required";
        if (!inchargeName.trim()) allErrors.inchargeName = "Incharge name is required";
        if (!inchargeWhatsapp.trim()) allErrors.inchargeWhatsapp = "Whatsapp number is required";
        else if (!validatePhone(inchargeWhatsapp)) allErrors.inchargeWhatsapp = "Enter a valid mobile number (10 digits)";
        if (!schoolEmail.trim()) allErrors.schoolEmail = "School email is required";
        else if (!validateEmail(schoolEmail)) allErrors.schoolEmail = "Enter a valid email address";

        // Quiz
        if (quizA.length > 0 && quizA.length !== 4) allErrors.quizA = "Group A quiz participants must be exactly 4";
        if (quizB.length > 0 && quizB.length !== 4) allErrors.quizB = "Group B quiz participants must be exactly 4";
        if (quizA.some((s) => !inRange(s.standard, 3, 5))) allErrors.quizAStd = "All Group A quiz students must be Std 3-5 (Group A only)";
        if (quizB.some((s) => !inRange(s.standard, 6, 9))) allErrors.quizBStd = "All Group B quiz students must be Std 6-9 (Group B only)";

        // Ensure at least one student from each standard in the group's range is present (final validation)
        if (quizA.length > 0) {
            const stdsA = new Set(quizA.map((s) => parseInt(s.standard, 10)).filter((n) => !Number.isNaN(n)));
            const requiredA = [3, 4, 5];
            const missingA = requiredA.filter((r) => !stdsA.has(r));
            if (missingA.length > 0) allErrors.quizAStdReq = "Group A quiz must include at least one student from each standard 3, 4 and 5";
        }
        if (quizB.length > 0) {
            const stdsB = new Set(quizB.map((s) => parseInt(s.standard, 10)).filter((n) => !Number.isNaN(n)));
            const requiredB = [6, 7, 8, 9];
            const missingB = requiredB.filter((r) => !stdsB.has(r));
            if (missingB.length > 1) allErrors.quizBStdReq = "Group B quiz must have students from at least three of these standards: 6, 7, 8, and 9.";
        }

        // Dance
        if (dance.length > 0 && (dance.length < 5 || dance.length > 12)) allErrors.dance = "Dance team must have 5-12 students (Group B only)";
        if (dance.some((s) => !inRange(s.standard, 6, 9))) allErrors.danceStd = "All Dance students must be Std 6-9 (Group B only)";

        // MCQ
        if (mcq.length > 20) allErrors.mcq = "Max 20 students allowed in MCQ (Group B only)";
        if (mcq.some((s) => !inRange(s.standard, 6, 9))) allErrors.mcqStd = "All MCQ students must be Std 6-9 (Group B only)";

        // Painting
        if (painting.length > 10) allErrors.painting = "Max 10 students allowed in Painting (Group A only)";
        if (painting.some((s) => !inRange(s.standard, 3, 5))) allErrors.paintingStd = "All Painting students must be Std 3-5 (Group A only)";

        // Art & Craft
        if (artCraft.length > 10) allErrors.artCraft = "Max 10 students allowed in Art & Craft (Group B only)";
        if (artCraft.some((s) => !inRange(s.standard, 6, 9))) allErrors.artCraftStd = "All Art & Craft students must be Std 6-9 (Group B only)";

        // cross-competition uniqueness
        const dupes = anyDuplicateAcross();
        if (dupes.length > 0) allErrors.duplicates = "A student cannot participate in more than one competition. Remove duplicates.";

        // NEW: duplicates within same competition
        const withinDupes = anyDuplicateWithin();
        if (withinDupes.length > 0) {
            // collect unique list names where duplicates were found
            const listsWithDupes = [...new Set(withinDupes.map(d => d.list))];
            allErrors.duplicatesWithin = `Duplicate student entries found within: ${listsWithDupes.join(', ')}. Remove duplicates.`;
        }

        // require at least one competition to be filled
        const quizTotal = (quizA.length + quizB.length) > 0;
        const anyCompetitionFilled = quizTotal || dance.length > 0 || mcq.length > 0 || painting.length > 0 || artCraft.length > 0;
        if (!anyCompetitionFilled) {
            allErrors.noCompetitions = "Please add participants for at least one competition before submitting.";
        }

        // per-student checks for all lists
        const sErrors = {
            ...(validateStudentList(quizA, 'quizA', 'A') || {}),
            ...(validateStudentList(quizB, 'quizB', 'B') || {}),
            ...(validateStudentList(dance, 'dance', 'B') || {}),
            ...(validateStudentList(mcq, 'mcq', 'B') || {}),
            ...(validateStudentList(painting, 'painting', 'A') || {}),
            ...(validateStudentList(artCraft, 'artCraft', 'B') || {}),
        };
        if (Object.keys(sErrors).length) allErrors.students = sErrors;

        setErrors(allErrors);
        return Object.keys(allErrors).length === 0;
    };

    const handleNext = () => {
        // Steps 2-6 are skip-able. validateStep will allow empty lists.
        if (!validateStep()) return;
        setActiveStep((prev) => prev + 1);
    };

    const handleBack = () => setActiveStep((prev) => prev - 1);

    const handleDelete = (list, setList, index) => {
        const updated = list.filter((_, i) => i !== index);
        setList(updated);
    };

    // New: render quiz as groups of 4 (Add Group / Delete Group)
    const renderQuizGroups = (list, setList, groupType, listKey) => {
        const groupsCount = Math.ceil(list.length / 4);
        const addGroup = () => {
            const four = Array.from({ length: 4 }, () => ({ ...emptyStudent }));
            setList([...list, ...four]);
        };
        const deleteGroup = (gIndex) => {
            const updated = [...list];
            updated.splice(gIndex * 4, 4);
            setList(updated);
        };

        return (
            <div>
                {Array.from({ length: groupsCount }).map((_, gIndex) => {
                    const start = gIndex * 4;
                    return (
                        <Card key={gIndex} className={classes.card}>
                            <CardContent>
                                <div className={classes.cardGroupGrid}>
                                    {Array.from({ length: 4 }).map((__, i) => {
                                        const idx = start + i;
                                        const student = list[idx] || { ...emptyStudent };
                                        const se = (errors.students && errors.students[listKey] && errors.students[listKey][idx]) || {};
                                        return (
                                            <div key={i} className={classes.cardContentGrid}>
                                                <TextField
                                                    label={`Student ${i + 1} Name`}
                                                    value={student.name}
                                                    onChange={(e) => {
                                                        const newList = [...list];
                                                        newList[idx] = { ...(newList[idx] || {}), name: e.target.value };
                                                        setList(newList);
                                                    }}
                                                    fullWidth
                                                    required
                                                    error={!!se.name}
                                                    helperText={se.name}
                                                    className={classes.field}
                                                />
                                                <TextField
                                                    label="Standard"
                                                    value={student.standard}
                                                    onChange={(e) => {
                                                        const newList = [...list];
                                                        newList[idx] = { ...(newList[idx] || {}), standard: e.target.value.replace(/\D/g, '') };
                                                        setList(newList);
                                                    }}
                                                    fullWidth
                                                    required
                                                    error={!!se.standard}
                                                    helperText={se.standard}
                                                    className={classes.field}
                                                />
                                                <TextField
                                                    label="Age"
                                                    value={student.age}
                                                    onChange={(e) => {
                                                        const newList = [...list];
                                                        newList[idx] = { ...(newList[idx] || {}), age: e.target.value.replace(/\D/g, '') };
                                                        setList(newList);
                                                    }}
                                                    fullWidth
                                                    required
                                                    error={!!se.age}
                                                    helperText={se.age}
                                                    className={classes.field}
                                                />
                                                <FormControl className={classes.field} fullWidth error={!!se.gender}>
                                                    <InputLabel>Gender</InputLabel>
                                                    <Select
                                                        value={student.gender || ''}
                                                        onChange={(e) => {
                                                            const newList = [...list];
                                                            newList[idx] = { ...(newList[idx] || {}), gender: e.target.value };
                                                            setList(newList);
                                                        }}
                                                        required
                                                        MenuProps={{
                                                            disableScrollLock: true
                                                        }}
                                                    >
                                                        <MenuItem value="Male">Male</MenuItem>
                                                        <MenuItem value="Female">Female</MenuItem>
                                                        <MenuItem value="Other">Other</MenuItem>
                                                    </Select>
                                                    {se.gender && <p className={classes.errorText}>{se.gender}</p>}
                                                </FormControl>
                                            </div>
                                        );
                                    })}
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                                    <Button color="secondary" onClick={() => deleteGroup(gIndex)}>
                                        Delete Group
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    );
                })}

                { groupsCount === 0 && <Button variant="outlined" onClick={addGroup} className={classes.addButton}>
                    Add Group
                </Button>
                }       
            </div>
        );
    };

    const renderStudentInputs = (list, setList, groupType, listKey) => {
        // set limits per category
        const limits = {
            mcq: 20,
            painting: 10,
            artCraft: 10,
            // quiz & dance handled by their own renderers
        };
        const maxLimit = limits[listKey] || Infinity;
        const disableAdd = list.length >= maxLimit;

        return (
            <div>
                {list.map((student, index) => {
                    const se = (errors.students && errors.students[listKey] && errors.students[listKey][index]) || {};
                    return (
                        <Card key={index} className={classes.card}>
                            <CardContent>
                                <div className={classes.cardContentGrid}>
                                    <TextField
                                        label="Name"
                                        value={student.name}
                                        onChange={(e) => {
                                            const newList = [...list];
                                            newList[index].name = e.target.value;
                                            setList(newList);
                                        }}
                                        className={classes.field}
                                        required
                                        error={!!se.name}
                                        helperText={se.name}
                                    />

                                    <TextField
                                        label="Standard"
                                        value={student.standard}
                                        onChange={(e) => {
                                            const newList = [...list];
                                            newList[index].standard = e.target.value.replace(/\D/g, '');
                                            setList(newList);
                                        }}
                                        error={!!se.standard}
                                        helperText={se.standard}
                                        className={classes.field}
                                        required
                                    />

                                    <TextField
                                        label="Age"
                                        value={student.age}
                                        onChange={(e) => {
                                            const newList = [...list];
                                            newList[index].age = e.target.value.replace(/\D/g, '');
                                            setList(newList);
                                        }}
                                        className={classes.field}
                                        required
                                        error={!!se.age}
                                        helperText={se.age}
                                    />

                                    <FormControl className={classes.field} error={!!se.gender}>
                                        <InputLabel>Gender</InputLabel>
                                        <Select
                                            value={student.gender}
                                            label="Gender"
                                            onChange={(e) => {
                                                const newList = [...list];
                                                newList[index].gender = e.target.value;
                                                setList(newList);
                                            }}
                                            required
                                            MenuProps={{
                                                disableScrollLock: true
                                            }}
                                        >
                                            <MenuItem value="Male">Male</MenuItem>
                                            <MenuItem value="Female">Female</MenuItem>
                                            <MenuItem value="Other">Other</MenuItem>
                                        </Select>
                                        {se.gender && <p className={classes.errorText}>{se.gender}</p>}
                                    </FormControl>
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                                    <Button
                                        className={classes.secondaryButton}
                                        onClick={() => handleDelete(list, setList, index)}
                                    >
                                        Delete
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    );
                })}

                <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginTop: 8 }}>
                    <Button
                        variant="outlined"
                        onClick={() => setList([...list, { ...emptyStudent }])}
                        className={classes.addButton}
                        disabled={disableAdd}
                    >
                        Add Student
                    </Button>

                    {Number.isFinite(maxLimit) && (
                        <div style={{ color: '#6b7280' }}>
                            {list.length} / {maxLimit} students
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // New: renderDanceGroup — adds team (5), then allows adding single students up to 12.
    const renderDanceGroup = (list, setList, groupType, listKey) => {
        const addTeam = () => {
            const five = Array.from({ length: 5 }, () => ({ ...emptyStudent }));
            setList([...list, ...five]);
        };
        const addStudent = () => {
            if (list.length >= 12) return;
            setList([...list, { ...emptyStudent }]);
        };
        const deleteStudentAt = (idx) => {
            // prevent dropping below minimum 5
            if (list.length <= 5) return;
            const updated = [...list];
            updated.splice(idx, 1);
            setList(updated);
        };
        const deleteTeam = () => setList([]);

        return (
            <div>
                {list.length === 0 ? (
                    <Button variant="outlined" onClick={addTeam} className={classes.addButton}>
                        Add Dance Team
                    </Button>
                ) : (
                    <>
                        <div>
                            {list.map((student, index) => {
                                const se = (errors.students && errors.students[listKey] && errors.students[listKey][index]) || {};
                                return (
                                    <Card key={index} className={classes.card}>
                                        <CardContent>
                                            <div className={classes.cardContentGrid}>
                                                <TextField
                                                    label={`Student ${index + 1} Name`}
                                                    value={student.name}
                                                    onChange={(e) => {
                                                        const newList = [...list];
                                                        newList[index] = { ...(newList[index] || {}), name: e.target.value };
                                                        setList(newList);
                                                    }}
                                                    fullWidth
                                                    required
                                                    error={!!se.name}
                                                    helperText={se.name}
                                                    className={classes.field}
                                                />
                                                <TextField
                                                    label="Standard"
                                                    value={student.standard}
                                                    onChange={(e) => {
                                                        const newList = [...list];
                                                        newList[index] = { ...(newList[index] || {}), standard: e.target.value.replace(/\D/g, '') };
                                                        setList(newList);
                                                    }}
                                                    fullWidth
                                                    required
                                                    error={!!se.standard}
                                                    helperText={se.standard}
                                                    className={classes.field}
                                                />
                                                <TextField
                                                    label="Age"
                                                    value={student.age}
                                                    onChange={(e) => {
                                                        const newList = [...list];
                                                        newList[index] = { ...(newList[index] || {}), age: e.target.value.replace(/\D/g, '') };
                                                        setList(newList);
                                                    }}
                                                    fullWidth
                                                    required
                                                    error={!!se.age}
                                                    helperText={se.age}
                                                    className={classes.field}
                                                />
                                                <FormControl className={classes.field} fullWidth error={!!se.gender}>
                                                    <InputLabel>Gender</InputLabel>
                                                    <Select
                                                        value={student.gender || ''}
                                                        onChange={(e) => {
                                                            const newList = [...list];
                                                            newList[index] = { ...(newList[index] || {}), gender: e.target.value };
                                                            setList(newList);
                                                        }}
                                                        required
                                                        MenuProps={{
                                                            disableScrollLock: true
                                                        }}
                                                    >
                                                        <MenuItem value="Male">Male</MenuItem>
                                                        <MenuItem value="Female">Female</MenuItem>
                                                        <MenuItem value="Other">Other</MenuItem>
                                                    </Select>
                                                    {se.gender && <p className={classes.errorText}>{se.gender}</p>}
                                                </FormControl>
                                            </div>

                                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                                                <Button
                                                    color="secondary"
                                                    onClick={() => deleteStudentAt(index)}
                                                    disabled={list.length <= 5} // prevent dropping below minimum
                                                >
                                                    Delete Student
                                                </Button>
                                            </div>
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </div>

                        <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                            <Button
                                variant="outlined"
                                onClick={addStudent}
                                disabled={list.length >= 12}
                            >
                                {list.length === 0 ? 'Add Dance Team' : 'Add student'}
                            </Button>

                            <div style={{ alignSelf: 'center', marginLeft: 8, color: '#6b7280' }}>
                                {list.length} / 12 students
                            </div>

                            <Button
                                variant="text"
                                color="secondary"
                                onClick={deleteTeam}
                            >
                                Delete this team
                            </Button>
                        </div>
                    </>
                )}
            </div>
        );
    };

    const renderStepContent = (key) => {
        switch (key) {
            case 0:
                return (
                    <div className={classes.stepContent}>
                        <div className={classes.rulesBox}>
                            <RulesContent />
                        </div>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={consentChecked}
                                    onChange={(e) => {
                                        setConsentChecked(e.target.checked);
                                        if (errors.consent) {
                                            setErrors(prev => { const copy = { ...prev }; delete copy.consent; return copy; });
                                        }
                                    }}
                                    color="primary"
                                />
                            }
                            label="I have read carefully and agree to the Rules and Terms & Conditions"
                        />
                        {errors.consent && <p className={classes.errorText}>{errors.consent}</p>}
                    </div>
                );

            case 1:
                return (
                    <div className={classes.stepContent}>
                        <Autocomplete
                            id="schoolname-autocomplete"
                            name="schoolname"
                            disablePortal
                            forcePopupIcon
                            fullWidth
                            options={schoolList || []}
                            getOptionLabel={(option) =>
                                typeof option === 'string'
                                    ? option
                                    : `${option.name || ''}${option.locality ? ', ' + option.locality : ''}`
                            }
                            value={schoolName}
                            onChange={(event, value) => {
                                if (value && typeof value === 'object') {
                                    setSchoolName(value.name || '');
                                    setErrors(prevErrors => ({ ...prevErrors , schoolName: null }));
                                } else if (typeof value === 'string') {
                                    setSchoolName(value);
                                    setErrors(prevErrors => ({ ...prevErrors , schoolName: null }));
                                } else {
                                    setSchoolName('');
                                }
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    required
                                    label="School Name"
                                    error={!!errors.schoolName}
                                    helperText={errors.schoolName}
                                />
                            )}
                        />

                        <TextField
                            fullWidth
                            label="Competition Incharge Name"
                            value={inchargeName}
                            onChange={(e) => {
                                setInchargeName(e.target.value)
                                setErrors(prevErrors => ({ ...prevErrors , inchargeName: null }));
                            }}
                            margin="normal"
                            required
                            error={!!errors.inchargeName}
                            helperText={errors.inchargeName}
                        />

                        <TextField
                            fullWidth
                            label="Whatsapp No. of Incharge"
                            value={inchargeWhatsapp}
                            onChange={(e) => {
                                setInchargeWhatsapp(e.target.value.replace(/\D/g, ''))
                                setErrors(prevErrors => ({ ...prevErrors , inchargeWhatsapp: null }));
                            }}
                            margin="normal"
                            inputProps={{ maxLength: 15 }}
                            required
                            error={!!errors.inchargeWhatsapp}
                            helperText={errors.inchargeWhatsapp}
                        />

                        <TextField
                            fullWidth
                            label="School Email Id"
                            value={schoolEmail}
                            onChange={(e) => {
                                setSchoolEmail(e.target.value)
                                setErrors(prevErrors => ({ ...prevErrors , schoolEmail: null }));
                            }}
                            margin="normal"
                            type="email"
                            required
                            error={!!errors.schoolEmail}
                            helperText={errors.schoolEmail}
                        />
                    </div>
                );

            case 2:
                return (
                    <div className={classes.stepContent}>
                        <QuizRulesContent />
                        <Typography className={classes.sectionTitle}>Group A (Std 3-5)</Typography>
                        {renderQuizGroups(quizA, setQuizA, "A", "quizA")}
                        {errors.quizA && <p className={classes.errorText}>{errors.quizA}</p>}
                        {errors.quizAStd && <p className={classes.errorText}>{errors.quizAStd}</p>}
                        {errors.quizAStdReq && <p className={classes.errorText}>{errors.quizAStdReq}</p>}
                        <br/>
                        <Typography className={classes.sectionTitle}>Group B (Std 6-9)</Typography>
                        {renderQuizGroups(quizB, setQuizB, "B", "quizB")}
                        {errors.quizB && <p className={classes.errorText}>{errors.quizB}</p>}
                        {errors.quizBStd && <p className={classes.errorText}>{errors.quizBStd}</p>}
                        {errors.quizBStdReq && <p className={classes.errorText}>{errors.quizBStdReq}</p>}

                        {errors.quiz && <p className={classes.errorText}>{errors.quiz}</p>}
                    </div>
                );

            case 3:
                return (
                    <div className={classes.stepContent}>
                        <DanceRulesContent />
                        <Typography className={classes.sectionTitle}>Dance / Act (Group B Only)</Typography>
                        {renderDanceGroup(dance, setDance, "B", "dance")}
                        {errors.dance && <p className={classes.errorText}>{errors.dance}</p>}
                        {errors.danceStd && <p className={classes.errorText}>{errors.danceStd}</p>}
                    </div>
                );

            case 4:
                return (
                    <div className={classes.stepContent}>
                        <McqRulesContent />
                        <Typography className={classes.sectionTitle}>MCQ (Group B Only)</Typography>
                        {renderStudentInputs(mcq, setMcq, "B", "mcq")}
                        {errors.mcq && <p className={classes.errorText}>{errors.mcq}</p>}
                        {errors.mcqStd && <p className={classes.errorText}>{errors.mcqStd}</p>}
                    </div>
                );

            case 5:
                return (
                    <div className={classes.stepContent}>
                        <PaintingRulesContent />
                        <Typography className={classes.sectionTitle}>Painting (Group A Only)</Typography>
                        {renderStudentInputs(painting, setPainting, "A", "painting")}
                        {errors.painting && <p className={classes.errorText}>{errors.painting}</p>}
                        {errors.paintingStd && <p className={classes.errorText}>{errors.paintingStd}</p>}
                    </div>
                );

            case 6:
                return (
                    <div className={classes.stepContent}>
                        <ArtCraftRulesContent />
                        <Typography className={classes.sectionTitle}>Art & Craft (Group B Only)</Typography>
                        {renderStudentInputs(artCraft, setArtCraft, "B", "artCraft")}
                        {errors.artCraft && <p className={classes.errorText}>{errors.artCraft}</p>}
                        {errors.artCraftStd && <p className={classes.errorText}>{errors.artCraftStd}</p>}
                    </div>
                );

            case steps.length - 1:
                return (
                    <div className={classes.stepContent}>
                        <Typography className={classes.sectionTitle}>Preview Registration</Typography>

                        <Card className={classes.card}>
                            <CardContent>
                                <Typography variant="subtitle1"><strong>School Details</strong></Typography>
                                <Typography>School Name: {schoolName || '—'}</Typography>
                                <Typography>Incharge: {inchargeName || '—'}</Typography>
                                <Typography>Whatsapp: {inchargeWhatsapp || '—'}</Typography>
                                <Typography>Email: {schoolEmail || '—'}</Typography>
                                <Typography>Agreed to Rules: {consentChecked ? 'Yes' : 'No'}</Typography>
                            </CardContent>
                        </Card>

                        <Card className={classes.card}>
                            <CardContent>
                                <Typography variant="subtitle1"><strong>Quiz - Group A</strong> ({quizA.length} student(s))</Typography>
                                {quizA.length === 0 ? <Typography>Not participating</Typography> :
                                    quizA.map((s, i) => (
                                        <Typography key={i}>{i+1}. {s.name} — Std {s.standard} — Age {s.age} — {s.gender}</Typography>
                                    ))
                                }
                                <hr/>
                                <Typography variant="subtitle1"><strong>Quiz - Group B</strong> ({quizB.length} student(s))</Typography>
                                {quizB.length === 0 ? <Typography>Not participating</Typography> :
                                    quizB.map((s, i) => (
                                        <Typography key={i}>{i+1}. {s.name} — Std {s.standard} — Age {s.age} — {s.gender}</Typography>
                                    ))
                                }
                            </CardContent>
                        </Card>

                        <Card className={classes.card}>
                            <CardContent>
                                <Typography variant="subtitle1"><strong>Dance / Act</strong> ({dance.length} student(s))</Typography>
                                {dance.length === 0 ? <Typography>Not participating</Typography> :
                                    dance.map((s, i) => (
                                        <Typography key={i}>{i+1}. {s.name} — Std {s.standard} — Age {s.age} — {s.gender}</Typography>
                                    ))
                                }
                            </CardContent>
                        </Card>

                        <Card className={classes.card}>
                            <CardContent>
                                <Typography variant="subtitle1"><strong>MCQ</strong> ({mcq.length} student(s))</Typography>
                                {mcq.length === 0 ? <Typography>Not participating</Typography> :
                                    mcq.map((s, i) => (
                                        <Typography key={i}>{i+1}. {s.name} — Std {s.standard} — Age {s.age} — {s.gender}</Typography>
                                    ))
                                }
                            </CardContent>
                        </Card>

                        <Card className={classes.card}>
                            <CardContent>
                                <Typography variant="subtitle1"><strong>Painting</strong> ({painting.length} student(s))</Typography>
                                {painting.length === 0 ? <Typography>Not participating</Typography> :
                                    painting.map((s, i) => (
                                        <Typography key={i}>{i+1}. {s.name} — Std {s.standard} — Age {s.age} — {s.gender}</Typography>
                                    ))
                                }
                            </CardContent>
                        </Card>

                        <Card className={classes.card}>
                            <CardContent>
                                <Typography variant="subtitle1"><strong>Art & Craft</strong> ({artCraft.length} student(s))</Typography>
                                {artCraft.length === 0 ? <Typography>Not participating</Typography> :
                                    artCraft.map((s, i) => (
                                        <Typography key={i}>{i+1}. {s.name} — Std {s.standard} — Age {s.age} — {s.gender}</Typography>
                                    ))
                                }
                            </CardContent>
                        </Card>

                        {errors.duplicates && <div className={classes.duplicatesBox}>{errors.duplicates}</div>}
                        {errors.duplicatesWithin && <div className={classes.duplicatesBox}>{errors.duplicatesWithin}</div>}
                        {errors.noCompetitions && <div className={classes.duplicatesBox}>{errors.noCompetitions}</div>}
                    </div>
                );

            default:
                return null;
        }
    };

    const tableWrapper = (rows) => `
    <table border="1" cellpadding="6" cellspacing="0" width="100%" style="border-collapse:collapse;">
        <tr style="background:#e8f0fe;">
            <th>Sr. No</th>
            <th>Name</th>
            <th>Standard</th>
            <th>Age</th>
            <th>Gender</th>
        </tr>
        ${rows}
    </table>`;

    const buildRows = (students) =>
      students
        .map(
          (s, index) => `
            <tr>
                <td>${index + 1}</td>
                <td>${s.name}</td>
                <td>${s.standard}</td>
                <td>${s.age}</td>
                <td>${s.gender}</td>
            </tr>`
        )
        .join("");

    const notParticipated = `
        <p style="font-style:italic; color:#777;">
            Not Participated
        </p>`;

    const sendEmail = () => {
        const emailParams = (to_email) => {
            return {
                schoolName,
                inchargeName,
                inchargeWhatsapp,
                to_email,
                quizA_table: quizA.length ? tableWrapper(buildRows(quizA)): notParticipated,
                quizB_table: quizB.length ? tableWrapper(buildRows(quizB)) : notParticipated,
                dance_table: dance.length ? tableWrapper(buildRows(dance)) : notParticipated,
                mcq_table: mcq.length ? tableWrapper(buildRows(mcq)) : notParticipated,
                painting_table: painting.length ? tableWrapper(buildRows(painting)) : notParticipated,
                artCraft_table: artCraft.length ? tableWrapper(buildRows(artCraft)) : notParticipated,
            }
        }

        emailjs.send("service_8m18sys", "template_uh4himl", emailParams(schoolEmail), "user_sY1m8Ki5Oafoc32NNK9TG");
        emailjs.send("service_8m18sys", "template_uh4himl", emailParams('hvid1997@gmail.com'), "user_sY1m8Ki5Oafoc32NNK9TG");
    };

    const handleSubmit = async (e) => {
        if (!validateAll()) return;

        // Final payload creation - ensure consistent data
        const payload = {
            consent: consentChecked,
            schoolName: schoolName.trim(),
            inchargeName: inchargeName.trim(),
            inchargeWhatsapp: inchargeWhatsapp.trim(),
            schoolEmail: schoolEmail.trim(),
            quizA,
            quizB,
            dance,
            mcq,
            painting,
            artCraft,
        };

        await axios({
            url: 'https://satprerna.org/satprerna_backend/home/sgp2025_competition_register.php',
            method: 'post',
            data: payload,
            responseType: 'json'
        })
        .then(response => {
            if(response.data.id !== undefined && response.data.id !== null && response.data.id !== '') {
                sendEmail(schoolEmail);
                // console.log('response', response.data)
                alert("Submitted Successfully. We will send you a confirmation on the provided school email ID.");
                window.location.reload();
            }
            else {
                console.log('err', response)
                alert("Something went wrong !");
            }
        })
        .catch(err => {
            console.log('err', err)
            alert("Something went wrong !");
        })
    };

    return (
        <div className={classes.rootouter}>
            <svg className={'wavy-divider ' + classes.divider} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 300" preserveAspectRatio="none" width="100%" height="100">
                <path d="M 1000 299 l 2 -279 c -155 -36 -310 135 -415 164 c -102.64 28.35 -149 -32 -232 -31 c -80 1 -142 53 -229 80 c -65.54 20.34 -101 15 -126 11.61 v 54.39 z"></path>
                <path d="M 1000 286 l 2 -252 c -157 -43 -302 144 -405 178 c -101.11 33.38 -159 -47 -242 -46 c -80 1 -145.09 54.07 -229 87 c -65.21 25.59 -104.07 16.72 -126 10.61 v 22.39 z"></path>
                <path d="M 1000 300 l 1 -230.29 c -217 -12.71 -300.47 129.15 -404 156.29 c -103 27 -174 -30 -257 -29 c -80 1 -130.09 37.07 -214 70 c -61.23 24 -108 15.61 -126 10.61 v 22.39 z"></path>
            </svg>
          <div className={classes.root}>
                <h6 className="t2" >School Competition Registration Form</h6>
                <h1 className="t1 mt-2 t1-sgp" >Shri Gita Prerna 2025</h1>
                <h4 className="mt-5" >Registration Closed</h4>
                {/* <p className='my-4'>
                    <b>Please refer to the below table carefully before filling form.</b> Ensure that all participants meet the eligibility criteria mentioned. Incomplete or incorrect forms may lead to disqualification.<br/>
                    For any queries, contact the event coordinator.
                </p>
                <img src={require("../images/instructions_sgp2025.png")} alt="Instructions" width='100%' />
                <p className="mb-4">
                    <b className={classes.redText}>Please note that a student cannot participate in more than one competition.</b><br />
                    <br /><b>School Competition Registration Form :</b><br />
                </p> */}

                {/* <Stepper
                    activeStep={activeStep}
                    orientation='vertical'
                    className={`${classes.stepper} ${classes.stepperVertical}`}
                >
                    {steps.map((label, key) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                            <StepContent>
                                <div>{renderStepContent(key)}</div>
                                <div className={classes.actionRow}>
                                    <Button disabled={activeStep === 0} onClick={handleBack}>
                                        Back
                                    </Button>

                                    {activeStep === steps.length - 1 ? (
                                        <Button className={classes.primaryButton} variant="contained" onClick={handleSubmit}>
                                            Submit
                                        </Button>
                                    ) : (
                                        <Button
                                            className={classes.primaryButton}
                                            variant="contained"
                                            onClick={handleNext}
                                            disabled={activeStep === 0 && !consentChecked}
                                        >
                                            Next
                                        </Button>
                                    )}
                                </div>
                            </StepContent>
                        </Step>
                    ))}
                </Stepper> */}
            </div>
        </div>
    );
}

function QuizRulesContent() {
  return (
    <div style={{ fontSize: '0.9rem'}}>
      <strong>Quiz competition</strong>:
      <ol type="a">
        <li>
          4 students can form a team (at least 1 student from each standard is
          mandatory) for the respective groups and can participate from 1
          school. Hence, maximum 8 students can compete from 1 school in groups
          A & B in this competition.
        </li>
        <li>
          A Preliminary round will be conducted initially of duration maximum 30
          minutes for all the teams of both the groups. Top 6 teams from each
          group will qualify for the Final round out of these Top 3 teams from
          each group will compete to be the 1st, 2nd and 3rd winners
          respectively.
        </li>
      </ol>
      <br />
    </div>
  );
}

function DanceRulesContent() {
  return (
    <div style={{ fontSize: '0.9rem'}}>
      <strong>Dance / Act competition</strong>:
      <ol type="a">
        <li>
          Minimum 5 and Maximum 12 students can form a team of Group B Only and
          can participate from 1 school in this competition.
        </li>
        <li>
          A Preliminary round will be conducted initially for the duration of
          maximum 5 minutes for all the teams in batches (e.g., 10 teams in each
          batch). Top 5 teams will qualify for the Final round, out of these Top
          3 teams will compete to be the 1st, 2nd and 3rd winners respectively.
        </li>
        <li>
          The theme of Dance/Act should be related to Shrimadbhagavat Gita or
          Shri Krishna Leela.
        </li>
      </ol>
      <br />
    </div>
  );
}

function McqRulesContent() {
  return (
    <div style={{ fontSize: '0.9rem'}}>
      <strong>MCQ competition</strong>:
      <ol type="a">
        <li>
          Maximum 20 students of Group B Only can participate from 1 school in
          this competition.
        </li>
        <li>
          A written competition of maximum 60 marks (45 Objectives based on 30
          Verses of Shrimad Bhagwad Gita Syllabus and 3 Subjective Questions
          based on Shri Gita related current affairs, general knowledge,
          Mahabharata and also Shri Gita related respectively) will be conducted
          of a maximum duration of 60 minutes for all the participants. Top 3
          Students will be awarded the 1st, 2nd and 3rd winners respectively.
        </li>
        <li>
          Answers or responses in English or Hindi languages will be acceptable.
        </li>
      </ol>
      <br />
    </div>
  );
}

function PaintingRulesContent() {
  return (
    <div style={{ fontSize: '0.9rem'}}>
      <strong>Painting competition</strong>:
      <ol type="a">
        <li>
          Maximum 10 students of Group A Only can participate from 1 school and
          can compete in this competition.
        </li>
        <li>
          This competition will be of a maximum duration of 60 minutes for all
          the participants. Top 3 Students will be awarded the 1st, 2nd and 3rd
          winners respectively.
        </li>
        <li>
          Participants can use pencil color, crayons, sketch pens, water color,
          charcoal as color and A3 size Chat paper for Painting.
        </li>
      </ol>
      <strong>Themes :</strong>
      <ul>
        <li>Kurukshetra battle field</li>
        <li>Shri Krishna Preaching Arjuna</li>
        <li>Shri Krishna lifting Wheel</li>
        <li>Virat Swaroop Darshan</li>
      </ul>
      <br />
    </div>
  );
}

function ArtCraftRulesContent() {
  return (
    <div style={{ fontSize: '0.9rem'}}>
      <strong>Art & Craft competition</strong>:
      <ol type="a">
        <li>
          Maximum 10 students of Group B Only can participate from 1 school and
          can compete in this competition.
        </li>
        <li>
          This competition will be of a maximum duration of 60 minutes for all
          the participants. Top 3 Students will be awarded the 1st, 2nd and 3rd
          winners respectively.
        </li>
        <li>
          Participants can use craft paper, glitters, glue etc decorative
          materials.
        </li>
      </ol>
      <strong>Theme:</strong>
      <ul>
        <li>Tree of Karma</li>
        <li>Krishna's Flute Pen-stand</li>
        <li>A puzzle of Krishna/Arjuna</li>
        <li>Gita Sloka Bracelet</li>
        <li>Paper Plate Krishna Face</li>
      </ul>
      <br />
    </div>
  );
}

function RulesContent() {
    return (
      <div>
        <h6><b>Please take a moment to carefully read the Rules & Regulations before proceeding:</b></h6>

        <ol>
          <li>
            Advance online registration is mandatory to participate in the
            competition and to attend it. There will be no facility of spot
            registration. Valid school ID cards & school uniforms will be
            considered as admit cards.
          </li>
          <li>
            Final Event would be on 13th Dec 2025 between 8 am to 5 pm at Doon
            Blossom Academy, Chandkheda. Breakfast and lunch will be provided
            free of cost by the organizers to all those attending the
            competition and programme.
          </li>
          <li>
            The minimum basic facilities required such as green room etc. will
            be provided during the programme at Doon Blossom Academy,
            Chandkheda.
          </li>
          <li>
            A student will be allowed to participate in a single competition. 5
            Types of competitions are as follows:
            <ul>
              <li>Quiz</li>
              <li>Dance / Act</li>
              <li>MCQ</li>
              <li>Painting</li>
              <li>Art & Craft</li>
            </ul>
          </li>
          <li>
            2 Groups: Group A - std 3rd to 5th whereas Group B - std 6th to 9th
            respectively.
          </li>
          <li>
            <strong>Quiz competition</strong>:
            <ol type="a">
              <li>
                4 students can form a team (at least 1 student from each
                standard is mandatory) for the respective groups and can
                participate from 1 school. Hence, maximum 8 students can compete
                from 1 school in groups A & B in this competition.
              </li>
              <li>
                A Preliminary round will be conducted initially of duration
                maximum 30 minutes for all the teams of both the groups. Top 6
                teams from each group will qualify for the Final round out of
                these Top 3 teams from each group will compete to be the 1st,
                2nd and 3rd winners respectively.
              </li>
            </ol>
          </li>
          <li>
            <strong>Dance / Act competition</strong>:
            <ol type="a">
              <li>
                Minimum 5 and Maximum 12 students can form a team of Group B
                Only and can participate from 1 school in this competition.
              </li>
              <li>
                A Preliminary round will be conducted initially for the duration
                of maximum 5 minutes for all the teams in batches (e.g., 10
                teams in each batch). Top 5 teams will qualify for the Final
                round, out of these Top 3 teams will compete to be the 1st, 2nd
                and 3rd winners respectively.
              </li>
              <li>
                The theme of Dance/Act should be related to Shrimadbhagavat Gita
                or Shri Krishna Leela.
              </li>
            </ol>
          </li>
          <li>
            <strong>MCQ competition</strong>:
            <ol type="a">
              <li>
                Maximum 20 students of Group B Only can participate from 1
                school in this competition.
              </li>
              <li>
                A written competition of maximum 60 marks (45 Objectives based
                on 30 Verses of Shrimad Bhagwad Gita Syllabus and 3 Subjective
                Questions based on Shri Gita related current affairs, general
                knowledge, Mahabharata and also Shri Gita related respectively)
                will be conducted of a maximum duration of 60 minutes for all
                the participants. Top 3 Students will be awarded the 1st, 2nd
                and 3rd winners respectively.
              </li>
              <li>
                Answers or responses in English or Hindi languages will be
                acceptable.
              </li>
            </ol>
          </li>
          <li>
            <strong>Painting competition</strong>:
            <ol type="a">
              <li>
                Maximum 10 students of Group A Only can participate from 1
                school and can compete in this competition.
              </li>
              <li>
                This competition will be of a maximum duration of 60 minutes for
                all the participants. Top 3 Students will be awarded the 1st,
                2nd and 3rd winners respectively.
              </li>
              <li>
                Participants can use pencil color, crayons, sketch pens, water
                color, charcoal as color and A3 size Chat paper for Painting.
              </li>
            </ol>
            <strong>Themes :</strong>
            <ul>
              <li>Kurukshetra battle field</li>
              <li>Shri Krishna Preaching Arjuna</li>
              <li>Shri Krishna lifting Wheel</li>
              <li>Virat Swaroop Darshan</li>
            </ul>
          </li>
          <li>
            <strong>Art & Craft competition</strong>:
            <ol type="a">
              <li>
                Maximum 10 students of Group B Only can participate from 1
                school and can compete in this competition.
              </li>
              <li>
                This competition will be of a maximum duration of 60 minutes for
                all the participants. Top 3 Students will be awarded the 1st,
                2nd and 3rd winners respectively.
              </li>
              <li>
                Participants can use craft paper, glitters, glue etc decorative
                materials.
              </li>
            </ol>
            <strong>Theme:</strong>
            <ul>
              <li>Tree of Karma</li>
              <li>Krishna's Flute Pen-stand</li>
              <li>A puzzle of Krishna/Arjuna</li>
              <li>Gita Sloka Bracelet</li>
              <li>Paper Plate Krishna Face</li>
            </ul>
          </li>
          <li>
            All the participants of this competition will receive a
            participation certificate.
          </li>
          <li>
            1st, 2nd and 3rd winners of all the competitions will be awarded
            Gold, Silver and Bronze Medals and Winner certificates.
          </li>
          <li>
            All the participating schools will be awarded with a Trophy and a
            certificate of appreciation.
          </li>
          <li>
            Evaluation of answer sheets and other competitions will be carried
            out by the team of eminent personalities of society.
          </li>
          <li>
            Teachers/Parents/guardians and companions of the participants can
            enjoy the stage program with no interference in the competition or
            judges' decisions.
          </li>
          <li>
            All the participating students, teachers, parents, principals and
            their companions indemnify Satprerna Trust and Doon Blossom Academy,
            Chandkheda from any damages and losses during programme including
            third party during the period of this programme and will have to
            attend this programme on their own expenses and personal
            responsibilities and will exercise utmost care and discipline during
            their presence in Doon Blossom Academy.
          </li>
          <li>
            The reasonable terms and conditions of this competition and
            programme can be suitably modified by Satprerna Trust and Doon
            Blossom Academy, Chandkheda any time and will be acceptable and
            binding on all the stakeholders.
          </li>
          <li>
            All the decisions of Satprerna Trust regarding any disputes and
            otherwise pertaining to this programme will be final and binding on
            all the stakeholders.
          </li>
        </ol>
      </div>
    );
}
